import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { logAudit } from '../auditLog';
import { Empty, Icon, Loading, Modal, Switch, useToast } from '../ui';

// Seçenek şablonları: bir seçenek grubu (ör. "Porsiyon") bir kez tanımlanır ve
// seçilen ürünlere kopyalanır. Ürünlerdeki kopya `templateId` taşır; şablon
// değişince bağlı ürünler de güncellenir. Uygulama ve web sitesi yalnızca
// ürünün içindeki `options` alanını okur, bu yüzden onlarda değişiklik gerekmez.

const templatesCol = () => collection(db, 'restaurants', RESTAURANT_ID, 'optionTemplates');
const productRef = (id) => doc(db, 'restaurants', RESTAURANT_ID, 'products', id);

const optionLabel = (items) => (items || []).map((it) => `${it.name}${Number(it.extraPrice) > 0 ? ` (+${it.extraPrice} TL)` : ''}`).join(' · ');
const isLinked = (p, templateId) => (p.options || []).some((o) => o.templateId === templateId);

export async function loadOptionTemplates() {
  const snap = await getDocs(templatesCol());
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(a.title).localeCompare(String(b.title), 'tr'));
}

// Ürün listesine toplu yazma (Firestore toplu yazma sınırı 500).
async function commitProductUpdates(updates) {
  for (let i = 0; i < updates.length; i += 450) {
    const batch = writeBatch(db);
    updates.slice(i, i + 450).forEach(({ id, options }) => batch.update(productRef(id), { options }));
    await batch.commit();
  }
}

export default function OptionTemplates({ products, categories, onProductsChanged }) {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { setTemplates(await loadOptionTemplates()); } catch { toast('Şablonlar yüklenemedi.', 'error'); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const linkedCount = (t) => products.filter((p) => isLinked(p, t.id)).length;

  const save = async (form, selectedIds) => {
    setBusy(true);
    try {
      const data = { title: form.title.trim(), required: !!form.required, items: form.items, updatedAt: serverTimestamp() };
      let id = form.id;
      if (id) await updateDoc(doc(templatesCol(), id), data);
      else id = (await addDoc(templatesCol(), { ...data, createdAt: serverTimestamp() })).id;

      const group = { title: data.title, required: data.required, items: data.items, templateId: id };
      const updates = [];
      products.forEach((p) => {
        const opts = p.options || [];
        const linked = opts.some((o) => o.templateId === id);
        const selected = selectedIds.has(p.id);
        if (selected) updates.push({ id: p.id, options: linked ? opts.map((o) => (o.templateId === id ? group : o)) : [...opts, group] });
        else if (linked) updates.push({ id: p.id, options: opts.filter((o) => o.templateId !== id) });
      });
      await commitProductUpdates(updates);
      logAudit(form.id ? 'option_template_updated' : 'option_template_created', id, { products: selectedIds.size });
      toast(`“${data.title}” kaydedildi · ${selectedIds.size} ürüne uygulandı.`, 'ok');
      setEditing(null);
      await reload();
      onProductsChanged();
    } catch (e) {
      console.error(e);
      toast('Kaydedilemedi, tekrar deneyin.', 'error');
    }
    setBusy(false);
  };

  const remove = async (t) => {
    setBusy(true);
    try {
      const updates = products.filter((p) => isLinked(p, t.id))
        .map((p) => ({ id: p.id, options: (p.options || []).filter((o) => o.templateId !== t.id) }));
      await commitProductUpdates(updates);
      await deleteDoc(doc(templatesCol(), t.id));
      logAudit('option_template_deleted', t.id, { products: updates.length });
      toast(`“${t.title}” kaldırıldı.`, 'ok');
      await reload();
      onProductsChanged();
    } catch {
      toast('Kaldırılamadı, tekrar deneyin.', 'error');
    }
    setBusy(false);
    setConfirmDelete(null);
  };

  if (loading) return <div className="k-card"><Loading text="Şablonlar yükleniyor…" /></div>;

  return (
    <>
      <div className="k-card">
        <div className="kt-head">
          <p>Birden fazla üründe aynı seçenek varsa (ör. porsiyon, acı tercihi) bir kez tanımlayıp istediğiniz ürünlere uygulayın. Şablonu değiştirdiğinizde bağlı ürünlerin hepsi güncellenir.</p>
          <button type="button" className="k-btn k-btn-primary" onClick={() => setEditing('new')}><Icon name="plus" size={16} />Şablon ekle</button>
        </div>
        {templates.length === 0 ? (
          <Empty icon="dish" title="Henüz şablon yok">“Şablon ekle” ile ilk seçenek grubunuzu oluşturun.</Empty>
        ) : (
          <table className="k-table">
            <thead><tr><th>Şablon</th><th>Seçenekler</th><th>Zorunlu</th><th>Bağlı ürün</th><th /></tr></thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.title}</td>
                  <td>{optionLabel(t.items)}</td>
                  <td>{t.required ? 'Evet' : 'Hayır'}</td>
                  <td className="tnum">{linkedCount(t)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => setEditing(t)}>Düzenle</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <TemplateForm
          template={editing === 'new' ? null : editing}
          products={products}
          categories={categories}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={save}
          onDelete={(t) => { setEditing(null); setConfirmDelete(t); }}
        />
      )}

      {confirmDelete && (
        <Modal
          title="Şablonu kaldır"
          width={440}
          onClose={() => setConfirmDelete(null)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setConfirmDelete(null)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-danger-solid" disabled={busy} onClick={() => remove(confirmDelete)}>Kaldır</button>
            </>
          )}
        >
          “{confirmDelete.title}” şablonu kaldırılacak ve bağlı <strong>{linkedCount(confirmDelete)} üründen</strong> bu seçenek grubu silinecek.
        </Modal>
      )}

      <style>{`
        .kt-head { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 14px 16px; border-bottom: 1px solid var(--k-line); }
        .kt-head p { margin: 0; font-size: 13px; color: #111; max-width: 680px; }
        .kt-products { border: 1px solid var(--k-line); border-radius: 6px; max-height: 320px; overflow-y: auto; }
        .kt-cat { border-bottom: 1px solid var(--k-line); }
        .kt-cat:last-child { border-bottom: 0; }
        .kt-cat-head { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: var(--k-subtle); font-size: 13px; font-weight: 600; position: sticky; top: 0; }
        .kt-cat-head button { border: 0; background: none; font-size: 12.5px; font-weight: 600; color: #111; text-decoration: underline; padding: 0; }
        .kt-prod { display: flex; align-items: center; gap: 10px; padding: 7px 12px; font-size: 13.5px; cursor: pointer; }
        .kt-prod:hover { background: #FAFBFA; }
        .kt-prod input { width: 16px; height: 16px; accent-color: #111; }
        .kt-prod em { margin-left: auto; font-style: normal; font-size: 12px; }
        .kt-sel-bar { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 8px; }
      `}</style>
    </>
  );
}

function TemplateForm({ template, products, categories, busy, onClose, onSave, onDelete }) {
  const toast = useToast();
  const [form, setForm] = useState({
    id: template?.id || null,
    title: template?.title || '',
    required: template ? !!template.required : true,
    items: template?.items ? template.items.map((it) => ({ ...it })) : [],
  });
  const [item, setItem] = useState({ name: '', extraPrice: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set(template ? products.filter((p) => isLinked(p, template.id)).map((p) => p.id) : []));

  const groups = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    const names = [...categories.map((c) => c.name), ...new Set(products.map((p) => p.category).filter((c) => !categories.some((x) => x.name === c)))];
    return names.map((name) => ({
      name,
      items: products.filter((p) => p.category === name && (!q || String(p.name).toLocaleLowerCase('tr-TR').includes(q))),
    })).filter((g) => g.items.length > 0);
  }, [products, categories, search]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleGroup = (g) => setSelected((s) => {
    const n = new Set(s);
    const all = g.items.every((p) => n.has(p.id));
    g.items.forEach((p) => (all ? n.delete(p.id) : n.add(p.id)));
    return n;
  });

  const addItem = () => {
    const name = item.name.trim();
    if (!name) return;
    if (form.items.some((it) => it.name === name)) { toast('Bu seçenek zaten var.', 'error'); return; }
    setForm((f) => ({ ...f, items: [...f.items, { name, extraPrice: Math.max(0, parseFloat(item.extraPrice) || 0) }] }));
    setItem({ name: '', extraPrice: '' });
  };

  const submit = () => {
    if (!form.title.trim()) { toast('Şablon adı zorunlu.', 'error'); return; }
    if (form.items.length < 2) { toast('En az iki seçenek ekleyin.', 'error'); return; }
    onSave(form, selected);
  };

  return (
    <Modal
      title={template ? 'Şablonu düzenle' : 'Yeni seçenek şablonu'}
      width={620}
      onClose={onClose}
      footer={(
        <>
          {template && <button type="button" className="k-btn k-btn-danger" style={{ marginRight: 'auto' }} onClick={() => onDelete(template)}>Şablonu kaldır</button>}
          <button type="button" className="k-btn k-btn-secondary" onClick={onClose}>Vazgeç</button>
          <button type="button" className="k-btn k-btn-primary" disabled={busy} onClick={submit}>{busy ? 'Kaydediliyor…' : 'Kaydet ve uygula'}</button>
        </>
      )}
    >
      <label className="k-field"><span>Şablon adı (müşteri bu başlığı görür) *</span>
        <input className="k-input" placeholder="Örn. Porsiyon" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
      </label>
      <div style={{ marginBottom: 14 }}>
        <Switch checked={form.required} onChange={(v) => setForm((f) => ({ ...f, required: v }))} label="Müşteri seçmek zorunda" />
      </div>
      <div className="k-field"><span>Seçenekler</span>
        {form.items.map((it, i) => (
          <div key={it.name} className="km-option">
            <div>{it.name}{Number(it.extraPrice) > 0 && <span> · +{it.extraPrice} TL</span>}</div>
            <button type="button" className="k-icon-btn" title="Kaldır" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}><Icon name="x" size={16} /></button>
          </div>
        ))}
        <div className="km-inline">
          <input className="k-input" placeholder="Seçenek adı (ör. 1,5 porsiyon)" value={item.name} onChange={(e) => setItem((p) => ({ ...p, name: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && addItem()} />
          <input className="k-input" style={{ width: 120 }} placeholder="+ TL" type="number" min="0" value={item.extraPrice} onChange={(e) => setItem((p) => ({ ...p, extraPrice: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && addItem()} />
          <button type="button" className="k-btn k-btn-secondary" onClick={addItem}><Icon name="plus" size={15} />Ekle</button>
        </div>
      </div>

      <div className="k-field"><span>Uygulanacak ürünler</span>
        <div className="kt-sel-bar">
          <div className="k-search" style={{ flex: 1 }}>
            <Icon name="search" size={15} />
            <input className="k-input" placeholder="Ürün ara" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <strong className="tnum" style={{ fontSize: 13 }}>{selected.size} ürün seçili</strong>
        </div>
        <div className="kt-products">
          {groups.length === 0 ? <div style={{ padding: 12, fontSize: 13 }}>Eşleşen ürün yok.</div> : groups.map((g) => (
            <div key={g.name} className="kt-cat">
              <div className="kt-cat-head">
                <span>{g.name}</span>
                <button type="button" onClick={() => toggleGroup(g)}>{g.items.every((p) => selected.has(p.id)) ? 'Hiçbirini seçme' : 'Tümünü seç'}</button>
              </div>
              {g.items.map((p) => {
                const other = (p.options || []).filter((o) => o.templateId !== form.id && o.title === form.title.trim()).length > 0;
                return (
                  <label key={p.id} className="kt-prod">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    <span>{p.name}</span>
                    {other && <em>Aynı adlı başka grup var</em>}
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
